import { useState, useEffect, useMemo } from "react";
import { Loader2, Package, ListChecks } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { Modal } from "./SharedComponents";

// Fix O (2026-08-31): punto 4 de la carta (doble planilla) y pedido de Edson el
// 24/08 — los listados de Materiales y Tareas de la obra (budgets_listados.py)
// ya existían en el backend pero no tenían ninguna pantalla que los consumiera.
// Solo lectura: no modifica el presupuesto ni ningún cálculo.
export function ListadosObraModal({ budgetId, budgetNumber, onClose }) {
  const [tab, setTab] = useState("materiales");
  // Fix R (2026-08-31): interruptor entre el listado por ítem (con montos, tal
  // como estaba) y los materiales desglosados desde los componentes del
  // catálogo (sin montos) — misma fuente que ahora ofrece el pedido.
  const [vistaMateriales, setVistaMateriales] = useState("por_item");
  const [niveles, setNiveles] = useState([]);
  const [nivelId, setNivelId] = useState("");
  const [materiales, setMateriales] = useState(null);
  const [materialesExplotado, setMaterialesExplotado] = useState(null);
  const [tareas, setTareas] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/budgets/${budgetId}/items-para-pedido`, { withCredentials: true })
      .then(({ data }) => setNiveles(data.niveles || []))
      .catch(() => {});
  }, [budgetId]);

  useEffect(() => {
    setLoading(true);
    const params = nivelId ? { nivel_id: nivelId } : {};
    Promise.all([
      axios.get(`${API}/budgets/${budgetId}/listado-materiales`, { params, withCredentials: true }),
      axios.get(`${API}/budgets/${budgetId}/listado-materiales-explotado`, { params, withCredentials: true }),
      axios.get(`${API}/budgets/${budgetId}/listado-tareas`, { params, withCredentials: true }),
    ]).then(([mRes, mExplRes, tRes]) => {
      setMateriales(mRes.data);
      setMaterialesExplotado(mExplRes.data);
      setTareas(tRes.data);
    }).finally(() => setLoading(false));
  }, [budgetId, nivelId]);

  // El total mostrado se recalcula siempre a partir de las filas que devuelve el
  // servidor para el nivel elegido (los campos "total" de la respuesta no se
  // refiltran por nivel en el backend, así que sumamos las filas ya filtradas).
  const totalMateriales = useMemo(() => (materiales?.materiales || []).reduce((s, r) => s + (r.subtotal_mat || 0), 0), [materiales]);
  const totalFondoMo = useMemo(() => (tareas?.filas || []).reduce((s, f) => s + (f.fondo_mo || 0), 0), [tareas]);

  return (
    <Modal open onClose={onClose} title={`Listados de Obra · ${budgetNumber}`} wide>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-2" data-testid="listados-obra-tabs">
            <button
              onClick={() => setTab("materiales")}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === "materiales" ? "bg-[#2E6DA4] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid="listados-tab-materiales"
            >
              <Package size={14} /> Materiales
            </button>
            <button
              onClick={() => setTab("tareas")}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === "tareas" ? "bg-[#2E6DA4] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid="listados-tab-tareas"
            >
              <ListChecks size={14} /> Tareas
            </button>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mr-2">Nivel:</label>
            <select
              value={nivelId}
              onChange={e => setNivelId(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
              data-testid="listados-nivel-select"
            >
              <option value="">Todos los niveles</option>
              {niveles.map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="animate-spin" size={22} /></div>
        ) : tab === "materiales" ? (
          <div className="space-y-3" data-testid="listado-materiales-content">
            <div className="flex gap-2" data-testid="listado-materiales-vista-toggle">
              <button
                onClick={() => setVistaMateriales("por_item")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${vistaMateriales === "por_item" ? "bg-[#1E3A5F] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid="listado-vista-por-item-btn"
              >
                Por ítem del presupuesto
              </button>
              <button
                onClick={() => setVistaMateriales("desglosado")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${vistaMateriales === "desglosado" ? "bg-[#1E3A5F] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid="listado-vista-desglosado-btn"
              >
                Materiales desglosados
              </button>
            </div>
            {vistaMateriales === "por_item" ? (
              <>
                <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Descripción</th>
                        <th className="text-center px-2 py-2.5 font-semibold text-gray-600">UM</th>
                        <th className="text-right px-2 py-2.5 font-semibold text-gray-600">Cantidad</th>
                        <th className="text-right px-2 py-2.5 font-semibold text-gray-600">Costo Unit. Prom.</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-gray-600">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(materiales?.materiales || []).map((r, i) => (
                        <tr key={i} data-testid={`listado-material-row-${i}`}>
                          <td className="px-3 py-2 text-gray-800">{r.descripcion}</td>
                          <td className="px-2 py-2 text-center text-gray-500">{r.unidad}</td>
                          <td className="px-2 py-2 text-right text-gray-700">{r.cantidad}</td>
                          <td className="px-2 py-2 text-right text-gray-600">{formatPYG(r.costo_unit_promedio)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatPYG(r.subtotal_mat)}</td>
                        </tr>
                      ))}
                      {(materiales?.materiales || []).length === 0 && (
                        <tr><td colSpan={5} className="text-center py-8 text-gray-400">Sin materiales para este nivel</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end bg-blue-50 rounded-xl px-4 py-3 border border-blue-100">
                  <span className="text-sm font-bold text-[#2E6DA4]" data-testid="listado-materiales-total">Total: {formatPYG(totalMateriales)}</span>
                </div>
              </>
            ) : (
              <div className="space-y-3" data-testid="listado-materiales-desglosado-content">
                {(materialesExplotado?.items_explotados || 0) === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800" data-testid="listado-desglosado-sin-catalogo">
                    Ningún ítem de este presupuesto está vinculado a una boca del catálogo con componentes cargados,
                    así que no hay nada para desglosar todavía. Cargá el catálogo de bocas (con sus componentes) y
                    corré el autollenado para que la explosión de materiales funcione.
                  </div>
                ) : (
                  <div className="flex gap-3 text-xs" data-testid="listado-desglosado-resumen">
                    <span className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-3 py-1.5 font-semibold">
                      {materialesExplotado?.items_explotados || 0} ítem(s) explotados desde el catálogo
                    </span>
                    <span className="bg-gray-50 border border-gray-200 text-gray-600 rounded-lg px-3 py-1.5 font-semibold">
                      {materialesExplotado?.items_sin_desglose || 0} ítem(s) sin vínculo (van directo)
                    </span>
                  </div>
                )}
                <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Descripción</th>
                        <th className="text-center px-2 py-2.5 font-semibold text-gray-600">UM</th>
                        <th className="text-right px-3 py-2.5 font-semibold text-gray-600">Cantidad</th>
                        <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Origen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(materialesExplotado?.materiales || []).map((r, i) => (
                        <tr key={i} data-testid={`listado-desglosado-row-${i}`}>
                          <td className="px-3 py-2 text-gray-800">{r.descripcion}</td>
                          <td className="px-2 py-2 text-center text-gray-500">{r.unidad}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{r.cantidad}</td>
                          <td className="px-3 py-2 text-gray-400 text-[11px]">{(r.de_items || []).join(", ")}</td>
                        </tr>
                      ))}
                      {(materialesExplotado?.materiales || []).length === 0 && (
                        <tr><td colSpan={4} className="text-center py-8 text-gray-400">Sin materiales para este nivel</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="listado-tareas-content">
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 uppercase">Resumen por Tarea</div>
                <div className="max-h-48 overflow-y-auto">
                  {(tareas?.por_tarea || []).map((t, i) => (
                    <div key={i} className="flex justify-between px-3 py-1.5 text-xs border-b border-gray-50 last:border-0">
                      <span className="text-gray-700">{t.tarea_nombre}</span>
                      <span className="font-semibold text-gray-900">{formatPYG(t.fondo_mo)}</span>
                    </div>
                  ))}
                  {(tareas?.por_tarea || []).length === 0 && <p className="text-center py-4 text-gray-400 text-xs">Sin datos</p>}
                </div>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 text-xs font-bold text-gray-600 uppercase">Resumen por Nivel</div>
                <div className="max-h-48 overflow-y-auto">
                  {(tareas?.por_nivel || []).map((t, i) => (
                    <div key={i} className="flex justify-between px-3 py-1.5 text-xs border-b border-gray-50 last:border-0">
                      <span className="text-gray-700">{t.nivel_label}</span>
                      <span className="font-semibold text-gray-900">{formatPYG(t.fondo_mo)}</span>
                    </div>
                  ))}
                  {(tareas?.por_nivel || []).length === 0 && <p className="text-center py-4 text-gray-400 text-xs">Sin datos</p>}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-[35vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Tarea</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-gray-600">Nivel</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-gray-600">Fondo M.O.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(tareas?.filas || []).map((f, i) => (
                    <tr key={i} data-testid={`listado-tarea-row-${i}`}>
                      <td className="px-3 py-2 text-gray-800">{f.tarea_nombre}</td>
                      <td className="px-3 py-2 text-gray-500">{f.nivel_label}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{formatPYG(f.fondo_mo)}</td>
                    </tr>
                  ))}
                  {(tareas?.filas || []).length === 0 && (
                    <tr><td colSpan={3} className="text-center py-8 text-gray-400">Sin tareas para este nivel</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end bg-blue-50 rounded-xl px-4 py-3 border border-blue-100">
              <span className="text-sm font-bold text-[#2E6DA4]" data-testid="listado-tareas-total">Total Fondo M.O.: {formatPYG(totalFondoMo)}</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
